// frontend/src/utils/relationships/validateRelationshipNetworkCandidate.js

import {
  getGraphRelationDefinition,
  isAllowedAdditiveGraphRelation,
  normalizeGraphRelation,
} from './relationshipOntology';

const MIN_CONFIDENCE = 0.78;

const DESTRUCTIVE_WORDS = [
  'not',
  'no longer',
  'isn’t',
  "isn't",
  'wasn’t',
  "wasn't",
  'remove',
  'delete',
  'forget',
  'used to be',
];

function normalize(value = '') {
  return String(value || '').trim().toLowerCase();
}

function candidateTouchesResolvedAnchor(candidate = {}, resolvedAnchors = []) {
  const anchorIds = new Set(
    (Array.isArray(resolvedAnchors) ? resolvedAnchors : [])
      .map((anchor) => String(anchor?.entityId || '').trim())
      .filter(Boolean)
  );

  const fromEntityId = String(candidate?.fromEntityId || '').trim();
  const toEntityId = String(candidate?.toEntityId || '').trim();

  return (
    (fromEntityId && anchorIds.has(fromEntityId)) ||
    (toEntityId && anchorIds.has(toEntityId))
  );
}

function candidateTouchesRootUser({
  candidate = {},
  rootEntityIds = [],
  userName = '',
} = {}) {
  const rootIds = new Set(
    (Array.isArray(rootEntityIds) ? rootEntityIds : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  );
  const fromEntityId = String(candidate?.fromEntityId || '').trim();
  const toEntityId = String(candidate?.toEntityId || '').trim();

  if ((fromEntityId && rootIds.has(fromEntityId)) || (toEntityId && rootIds.has(toEntityId))) {
    return true;
  }

  const protectedNames = new Set(
    ['user', userName]
      .map(normalize)
      .filter(Boolean)
  );

  return (
    protectedNames.has(normalize(candidate?.fromName)) ||
    protectedNames.has(normalize(candidate?.toName))
  );
}

function looksDestructive(candidate = {}, message = '') {
  const relation = normalizeGraphRelation(candidate.relation);
  const sourceText = normalize(candidate.sourceText || message);

  if (relation.includes('remove') || relation.includes('delete') || relation.includes('forget')) {
    return true;
  }

  return DESTRUCTIVE_WORDS.some((word) => sourceText.includes(word));
}

export function validateRelationshipNetworkCandidate({
  candidate,
  resolvedAnchors = [],
  rootEntityIds = [],
  userName = '',
  message = '',
} = {}) {
  if (!candidate) {
    return { allowed: false, reason: 'Missing candidate.' };
  }

  const confidence = Number(candidate.confidence || 0);
  const relationshipContextBinding = candidate?.metadata?.relationshipContextBinding || null;
  const relationshipContextConfidence = Number(
    relationshipContextBinding?.confidence || 0
  );
  const stateBound = Boolean(
    relationshipContextBinding?.bound === true &&
      relationshipContextBinding?.possessorEntityId
  );
  const effectiveConfidence = stateBound
    ? Math.min(1, (confidence + relationshipContextConfidence) / 2)
    : confidence;
  const relation = normalizeGraphRelation(candidate.relation);
  const relationDefinition = getGraphRelationDefinition(relation);
  const fromEntityId = String(candidate.fromEntityId || '').trim();
  const toEntityId = String(candidate.toEntityId || '').trim();

  if (!candidate.fromName || !candidate.toName) {
    return { allowed: false, reason: 'Candidate missing fromName or toName.' };
  }

  if (!relation) {
    return { allowed: false, reason: 'Candidate missing relation.' };
  }

  if (effectiveConfidence < MIN_CONFIDENCE) {
    return {
      allowed: false,
      reason: `Confidence below threshold: ${effectiveConfidence}.`,
    };
  }

  if (!candidateTouchesResolvedAnchor(candidate, resolvedAnchors)) {
    return {
      allowed: false,
      reason: 'Candidate is not bound to a resolved entity identity.',
    };
  }

  if (candidateTouchesRootUser({ candidate, rootEntityIds, userName })) {
    return {
      allowed: false,
      requiresConfirmation: true,
      reason: 'USER↔Someone relationships must use the governed first-order confirmation path.',
    };
  }

  if (fromEntityId && toEntityId && fromEntityId === toEntityId) {
    return {
      allowed: false,
      reason: 'Candidate would create a self-edge on one entity identity.',
    };
  }

  const endpointResolution = candidate?.metadata?.endpointResolution || {};
  if (
    endpointResolution.from === 'ambiguous_existing_name' ||
    endpointResolution.to === 'ambiguous_existing_name'
  ) {
    return {
      allowed: false,
      requiresClarification: true,
      reason: 'A same-name endpoint is already ambiguous in the hydrated graph.',
    };
  }

  if (looksDestructive(candidate, message)) {
    return {
      allowed: false,
      requiresConfirmation: true,
      reason: 'Candidate appears destructive or corrective.',
    };
  }

  if (!isAllowedAdditiveGraphRelation(relation)) {
    return {
      allowed: false,
      reason: `Relation is not in additive allowlist: ${relation}.`,
    };
  }

  return {
    allowed: true,
    status: 'observed',
    authority: stateBound
      ? 'resolved_state_relationship_assertion'
      : 'explicit_user_relationship_assertion',
    confidence: effectiveConfidence,
    resolutionConfidence: Number(candidate.resolutionConfidence || 0.86),
    relationStability: relationDefinition?.stability || 'unknown',
    reason: 'Candidate is additive, non-root, identity-anchored, durable, and above confidence threshold.',
  };
}

export function validateRelationshipNetworkCandidates({
  candidates = [],
  resolvedAnchors = [],
  rootEntityIds = [],
  userName = '',
  message = '',
} = {}) {
  if (!Array.isArray(candidates) || candidates.length === 0) return [];

  return candidates.map((candidate) => ({
    candidate,
    validation: validateRelationshipNetworkCandidate({
      candidate,
      resolvedAnchors,
      rootEntityIds,
      userName,
      message,
    }),
  }));
}
